import { useEffect, useState, type ReactNode } from 'react';
import { useImages } from '@/lib/images';
import { HERO_VIDEO } from '@/data/images';

/**
 * Full-screen, full-bleed hero (Saxon-style).
 * - Plays a muted, looping background video sourced from the editable
 *   `heroVideo` slot (admin Images tab), falling back to the HERO_VIDEO default.
 * - Shows a full-screen poster image with a slow cinematic zoom when there is
 *   no video, the video fails to play, or the visitor prefers reduced motion —
 *   so the hero always looks intentional and never autoplays with sound.
 * A dark vignette keeps overlaid text legible.
 */
export function VideoHero({
  videoSrc,
  poster,
  children,
  minH = 'min-h-screen',
}: {
  videoSrc?: string; // optional override; defaults to the editable heroVideo slot
  poster: string;
  children: ReactNode;
  minH?: string;
}) {
  const img = useImages();
  const src = ((videoSrc ?? img.heroVideo) || '').trim() || HERO_VIDEO;

  const [videoOk, setVideoOk] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const showVideo = Boolean(src) && videoOk && !reduceMotion;

  return (
    <section className={`relative flex ${minH} items-center justify-center overflow-hidden bg-espresso-deep`}>
      {/* Media layer */}
      <div className="absolute inset-0">
        {showVideo ? (
          <video
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster}
            onError={() => setVideoOk(false)}
          >
            <source src={src} />
          </video>
        ) : (
          <img src={poster} alt="" className="kenburns h-full w-full object-cover" aria-hidden />
        )}
      </div>

      {/* Vignette for legibility */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-espresso-deep/85 via-espresso-deep/45 to-espresso-deep/55" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(80% 60% at 50% 30%, rgba(201,150,46,0.16), transparent 60%)' }}
      />

      {/* Content */}
      <div className="relative z-10 w-full">{children}</div>
    </section>
  );
}
